import { DefinedPropertyObject, IEntityMinimalModel } from "../../api/GenericTypes";
import { Sample, SampleFilters } from "../../api/Samples";
import {
  TabStoreFilterSidebarProps,
  TabStoreFilterFormProvider,
} from "../../common/sidebar/filtersidebar/TabStoreFilterFormProvider";
import { ProjectsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import { EntityFilterSideBar } from "../../common/sidebar/filtersidebar/EntityFilterBar";
import { CustomTypesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/CustomTypesVirtualizedSelectForm";
import {
  CustomFieldFilterLayout,
  CustomFieldFilterLayoutForm,
} from "../../Customization/CustomFieldFilters/CustomFieldFilterLayout/CustomFieldFilterLayout";
import {
  CombinedModificationRecordEntityDefaultFormValues,
  combinedModificationRecordEntityFiltersToForm,
  combinedModificationRecordEntityFormToFilters,
  ICombinedModificationRecordEntityForm,
} from "../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterFormModels";
import {
  ILockableEntityForm,
  LockableEntityDefaultFilterFormValues,
  lockableEntityFiltersToForm,
  lockableEntityFormToFilters,
} from "../../common/sidebar/filtersidebar/GenericFilters/LockableEntityFilterFormModels";
import {
  ISignableEntityForm,
  SignableEntityDefaultFilterFormValues,
  signableEntityFiltersToForm,
  signableEntityFormToFilters,
} from "../../common/sidebar/filtersidebar/GenericFilters/SignableEntityFilterFormModels";
import {
  ISoftDeletableEntityForm,
  SoftDeletableEntityDefaultFilterFormValues,
  softDeletableEntityFiltersToForm,
  softDeletableEntityFormToFilters,
} from "../../common/sidebar/filtersidebar/GenericFilters/SoftDeletableEntityFilterFormModels";
import { CombinedModificationRecordEntityFilterForm } from "../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterForm";
import { SignableEntityFilterForm } from "../../common/sidebar/filtersidebar/GenericFilters/SignableEntityFilterForm";
import { PersonsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/PersonsVirtualizedSelectForm";
import { defaultSamplesFilters } from "./SamplesTable";
import { FoldableFilters } from "../../common/sidebar/filtersidebar/GenericFilters/common/FoldableFilters";

export interface SampleFilterForm
  extends ICombinedModificationRecordEntityForm,
    ILockableEntityForm,
    ISignableEntityForm,
    ISoftDeletableEntityForm,
    CustomFieldFilterLayoutForm {
  project?: IEntityMinimalModel[] | undefined | null;
  ownerIds?: IEntityMinimalModel[] | undefined | null;
  customType?: { id: number; name: string }[] | undefined | null;
}
const defaultValues: DefinedPropertyObject<SampleFilterForm> = {
  project: null,
  customType: null,
  ownerIds: null,
  ...CombinedModificationRecordEntityDefaultFormValues,
  ...LockableEntityDefaultFilterFormValues,
  ...SignableEntityDefaultFilterFormValues,
  ...SoftDeletableEntityDefaultFilterFormValues,
  customFieldFilters: [],
};
export const samplesFormToFilters = (form: SampleFilterForm): SampleFilters => {
  let filters: SampleFilters = {
    ...combinedModificationRecordEntityFormToFilters(form),
    ...lockableEntityFormToFilters(form),
    ...signableEntityFormToFilters(form),
    ...softDeletableEntityFormToFilters(form),
  };
  filters.customTypeIds =
    Array.isArray(form.customType) && form.customType.length > 0 ? form.customType.map((d) => d.id) : null;
  filters.projectIds = Array.isArray(form.project) && form.project.length > 0 ? form.project?.map((d) => d.id) : null;
  filters.ownerIds = Array.isArray(form.ownerIds) && form.ownerIds.length > 0 ? form.ownerIds?.map((d) => d.id) : null;
  return filters;
};
/**
 * Converts SampleFilters to SampleFilterForm
 * @param filters SampleFilters
 * @returns SampleFilterForm
 * @author @CorradoSurmanowicz
 */
export const filtersToSamplesForm = (filters: SampleFilters): SampleFilterForm => {
  return {
    project: filters.projectIds ? filters.projectIds.map((id) => ({ id, name: "" })) : null,
    customType: filters.customTypeIds ? filters.customTypeIds.map((id) => ({ id, name: "" })) : null,
    ...combinedModificationRecordEntityFiltersToForm(filters),
    ...lockableEntityFiltersToForm(filters),
    ...signableEntityFiltersToForm(filters),
    ...softDeletableEntityFiltersToForm(filters),
  };
};

export const SampleFilterBar = ({
  initialValues,
  onFormChange,
  dispatchTabStore,
  tabsLoading,
  currentTab,
}: TabStoreFilterSidebarProps<Sample, SampleFilterForm, SampleFilters>) => {
  // const stateArray = [!!initialValues?.customType, !!initialValues?.project?.length, !!initialValues?.ownerIds?.length];

  return (
    <TabStoreFilterFormProvider
      tabsLoading={tabsLoading}
      initialValues={initialValues}
      defaultValues={defaultValues}
      defaultFilters={defaultSamplesFilters}
      formToFilters={samplesFormToFilters}
      onFormChange={onFormChange}
      dispatchTabStore={dispatchTabStore}
      customTypeConstraint="Sample"
    >
      {({ control, errors, onReset, customFieldsDict }) => (
        <EntityFilterSideBar onReset={onReset}>
          <FoldableFilters
            label="General"
            // defaultOpen={stateArray.some((p) => p) && !!stateArray.length}
            defaultOpen
          >
            <CustomTypesVirtualizedSelectForm
              id="customType"
              label="Sample types"
              placeholder="Select sample types..."
              control={control}
              showControls={false}
              filters={{ entityTypes: ["Sample"] }}
              isMulti
              enableBasicOption
              hideIfNoTypes
            />

            <ProjectsVirtualizedSelectForm id="project" control={control} showControls={false} isMulti />

            {currentTab !== "mydata" && (
              <PersonsVirtualizedSelectForm id="ownerIds" label="Owned by" control={control} isMulti />
            )}
          </FoldableFilters>
          {customFieldsDict && (
            <CustomFieldFilterLayout
              control={control}
              errors={errors}
              customFieldsDict={customFieldsDict}
              entityTypes={["Sample"]}
            />
          )}

          <CombinedModificationRecordEntityFilterForm
            control={control}
            currentTab={currentTab}
            initialValues={initialValues}
          />

          <SignableEntityFilterForm control={control} initialValues={initialValues} />

          {/* <SoftDeletableEntityFilterForm control={control} currentTab={currentTab} initialValues={initialValues} /> */}
        </EntityFilterSideBar>
      )}
    </TabStoreFilterFormProvider>
  );
};
